import { useEffect, useState } from "react";
import { CreditCard } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useGet } from "../lib/hooks";
import { RecordDialog } from "./RecordDialog";
import { Button } from "./ui/button";

const FIELDS = [
  { key: "billing_name", label: "Billing contact name" }, { key: "billing_email", label: "Billing email", type: "email" },
  { key: "billing_phone", label: "Billing phone" }, { key: "billing_abn", label: "ABN for invoices" },
  { key: "billing_address", label: "Billing address", full: true },
];

function CardOnFile() {
  const [d, reload] = useGet("/billing/card");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("card_session");
    if (!sid) return;
    window.history.replaceState({}, "", window.location.pathname);
    let n = 0;
    const poll = async () => {
      const { data } = await api.get(`/billing/card/status/${sid}`);
      if (data.status === "completed") { toast.success("Card saved"); reload(); }
      else if (++n < 8) setTimeout(poll, 2000);
    };
    poll().catch((e) => toast.error(errMsg(e)));
  }, [reload]);
  const add = async () => {
    setBusy(true);
    try { const { data } = await api.post("/billing/card/setup", { origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const remove = async () => { if (!window.confirm("Remove this card?")) return; await api.delete("/billing/card"); reload(); };
  if (!d) return null;
  const c = d.card;
  return (
    <div className="rounded-md border border-[#C2A462]/25 p-3 space-y-2" data-testid="card-on-file">
      <div className="flex items-center gap-2 text-sm"><CreditCard className="h-4 w-4 text-[#C2A462]" />
        {c ? <span data-testid="card-masked">{c.brand.toUpperCase()} •••• {c.last4} · expires {c.exp_month}/{String(c.exp_year).slice(-2)}</span> : <span className="text-muted-foreground">No card added yet</span>}
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={add} disabled={busy} data-testid="add-card-btn">{c ? "Replace card" : "Add credit card"}</Button>
        {c && <Button size="sm" variant="ghost" onClick={remove} data-testid="remove-card-btn">Remove</Button>}
      </div>
      <p className="text-[11px] text-slate-500">Card details are entered on Stripe's secure page and never stored in BOS HQ. Only the last 4 digits are shown.</p>
    </div>
  );
}

export function BillingDetailsCard() {
  const [b, reload] = useGet("/account/billing");
  const [edit, setEdit] = useState(false);
  if (!b) return null;
  const method = b.payment_method === "bank" ? `Bank transfer · ${b.payment_terms_days || 14} day terms` : "Credit card";
  return (
    <div className="rounded-md border bg-card p-5 space-y-2" data-testid="billing-details-card">
      <div className="overline">Billing details · shown on your invoices</div>
      {FIELDS.map((f) => <div key={f.key} className="flex justify-between text-sm border-b py-2 gap-3"><span className="text-muted-foreground">{f.label}</span><span className="text-right break-all" data-testid={`billing-${f.key}`}>{b[f.key] || "—"}</span></div>)}
      <div className="flex justify-between text-sm py-2 gap-3"><span className="text-muted-foreground">Payment method (set by BOS HQ)</span><span data-testid="billing-payment-method">{method}</span></div>
      {b.payment_method !== "bank" && <CardOnFile />}
      <Button size="sm" onClick={() => setEdit(true)} data-testid="edit-billing-details-btn">Update billing details</Button>
      {edit && <RecordDialog open onOpenChange={setEdit} title="Billing details" fields={FIELDS} initial={b}
        onSave={async (f) => { try { await api.put("/account/billing", f); toast.success("Billing details saved"); reload(); } catch (e) { toast.error(errMsg(e)); throw e; } }} />}
    </div>
  );
}
